import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { useT } from "@/lib/i18n";
import { HOME } from "@/constants/testIds";

const Manifesto = () => {
    const { lang } = useLang();
    const t = useT(lang);
    const eyebrow = t("manifesto.eyebrow");
    const title = t("manifesto.title");
    const statement = t("manifesto.statement");
    const chapters = t("manifesto.chapters");

    return (
        <section
            id="manifesto"
            data-testid={HOME.manifestoSection}
            className="relative py-28 md:py-40"
            style={{ background: "var(--bg-primary)" }}
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10">
                {/* Eyebrow */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="flex items-center gap-3 mb-12"
                >
                    <span className="w-8 h-px" style={{ background: "var(--ink-1)" }} />
                    <span className="font-body text-[11px] uppercase tracking-[0.32em]" style={{ color: "var(--ink-1)" }}>
                        {eyebrow}
                    </span>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-24">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="md:col-span-8 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight whitespace-pre-line"
                        style={{ color: "var(--ink-1)" }}
                    >
                        {title}{" "}
                        <span className="italic" style={{ color: "var(--pink)" }}>
                            {statement}
                        </span>
                    </motion.h2>
                </div>

                <div className="space-y-24 md:space-y-32">
                    {chapters.map((c, idx) => (
                        <motion.article
                            key={c.number}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: idx * 0.1 }}
                            className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start"
                        >
                            <div className="md:col-span-4">
                                <div className="chapter-num text-[120px] md:text-[180px]">{c.number}</div>
                                <div className="font-body text-[11px] uppercase tracking-[0.28em] mt-2" style={{ color: "var(--ink-mute)" }}>
                                    {lang === "es" ? "Capítulo" : "Chapter"} {c.number}
                                </div>
                            </div>
                            <div className="md:col-span-7 md:col-start-6">
                                <h3 className="font-serif text-3xl md:text-4xl leading-[1.1] mb-6 tracking-tight" style={{ color: "var(--ink-1)" }}>
                                    {c.title}
                                </h3>
                                <p className="font-body text-lg md:text-xl leading-[1.6]" style={{ color: "var(--ink-2)" }}>
                                    {c.body}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Manifesto;
